/**
 * Replay timeline (pure): what the replay worker collects on its first pass through the war —
 * every faction's victory points per hour and the big moments that become markers on the
 * scrubber (city captures, KESSELs, intercepts, capitulations, the outbreak and the end). Built
 * from the TRUE state and the unfiltered events, so it is the historian's record, not a staff's.
 */
import {
  type FactionId,
  type GameEvent,
  type GameMap,
  type HexGrid,
  NO_FACTION,
  type Tick,
} from '@friction/engine';
import { placeOf } from '../game/format';
import { inEveryLocale, type Localized, tr } from '../i18n';
import type { NationRef } from '../i18n/names';

export type MarkerKind = 'war' | 'capture' | 'kessel' | 'intercept' | 'capitulation' | 'end';

export interface ReplayMarker {
  /** Sequence number (stable key). */
  id: number;
  tick: Tick;
  kind: MarkerKind;
  /** Where to look, or -1. */
  hex: number;
  /** Faction whose colour the marker wears (captor, encircled, reader, capitulated), or -1. */
  faction: FactionId;
  /** One chronicle line in every language ("Varn taken by the Ostmarker"): the worker words it. */
  text: Localized<string>;
}

export interface ReplayNation {
  id: FactionId;
  name: string;
  adjective: string;
  color: number;
  team: number;
}

export interface ReplayTimeline {
  endTick: Tick;
  nations: ReplayNation[];
  /** Victory points held, per faction id, per tick: `vp[f][t]`. */
  vp: number[][];
  /** All VP on the map (the chart's full scale). */
  totalVp: number;
  markers: ReplayMarker[];
}

export class TimelineBuilder {
  private readonly map: GameMap;
  private readonly grid: HexGrid;
  private readonly nations: ReplayNation[];
  private readonly vp: number[][];
  private readonly markers: ReplayMarker[] = [];

  constructor(map: GameMap, grid: HexGrid, nations: readonly ReplayNation[]) {
    this.map = map;
    this.grid = grid;
    this.nations = nations.map(({ id, name, adjective, color, team }) => ({
      id,
      name,
      adjective,
      color,
      team,
    }));
    this.vp = nations.map(() => []);
  }

  private adj(f: FactionId): string | null {
    return this.nations.find((n) => n.id === f)?.adjective ?? null;
  }

  private nation(f: FactionId): NationRef | null {
    const n = this.nations.find((x) => x.id === f);
    return n ? { name: n.name, adjective: n.adjective } : null;
  }

  /** Record one position: the VP of every faction (by id) and the events of that tick. */
  add(tick: Tick, vpByFaction: readonly number[], events: readonly GameEvent[]): void {
    for (let f = 0; f < this.vp.length; f++) this.vp[f][tick] = vpByFaction[f] ?? 0;
    for (const ev of events) {
      const m = this.marker(ev);
      if (m) this.markers.push({ id: this.markers.length, ...m });
    }
  }

  finish(endTick: Tick): ReplayTimeline {
    // Ticks never visited (cannot happen on a full pass) read as the previous value.
    for (const series of this.vp) for (let t = 0; t <= endTick; t++) series[t] ??= t > 0 ? series[t - 1] : 0;
    return {
      endTick,
      nations: this.nations,
      vp: this.vp,
      totalVp: this.map.cities.reduce((a, c) => a + c.vp, 0),
      markers: this.markers.slice(),
    };
  }

  private marker(ev: GameEvent): Omit<ReplayMarker, 'id'> | null {
    const ctx = { map: this.map, grid: this.grid };
    switch (ev.kind) {
      case 'warDeclared':
        return {
          tick: ev.tick,
          kind: 'war',
          hex: -1,
          faction: -1,
          text: inEveryLocale((l) => tr(l)('mark.war', { theatre: ev.theatre })),
        };
      case 'cityCaptured': {
        const city = this.map.cities[ev.city];
        const p = {
          city: city?.name ?? null,
          to: this.adj(ev.to),
          from: this.adj(ev.from),
          owned: ev.from !== NO_FACTION,
        };
        return {
          tick: ev.tick,
          kind: 'capture',
          hex: city?.hex ?? -1,
          faction: ev.to,
          text: inEveryLocale((l) => tr(l)('mark.capture', p)),
        };
      }
      case 'encircled': {
        // One event per pocket goes to the encircled side; the copies for watching enemies list
        // only what they saw. Keep the owner's.
        if (ev.audience !== 'all' && !ev.audience.includes(ev.faction)) return null;
        const p = { n: ev.units.length, adj: this.adj(ev.faction), at: placeOf(ctx, ev.hex) };
        return {
          tick: ev.tick,
          kind: 'kessel',
          hex: ev.hex,
          faction: ev.faction,
          text: inEveryLocale((l) => tr(l)('mark.kessel', p)),
        };
      }
      case 'intercept': {
        const ic = ev.intercept;
        const reader = ev.audience === 'all' ? -1 : (ev.audience[0] ?? -1);
        const placeless = ic.order.kind === 'hold' || ic.order.kind === 'posture';
        const p = {
          reader: this.adj(reader),
          method: ic.method,
          unit: ic.unitName,
          kind: ic.order.kind,
          at: placeless ? null : placeOf(ctx, ic.order.target),
        };
        return {
          tick: ev.tick,
          kind: 'intercept',
          hex: ic.order.target,
          faction: reader,
          text: inEveryLocale((l) => tr(l)('mark.intercept', p)),
        };
      }
      case 'capitulated': {
        const p = { nation: this.nation(ev.faction), by: this.nation(ev.by) };
        return {
          tick: ev.tick,
          kind: 'capitulation',
          hex: -1,
          faction: ev.faction,
          text: inEveryLocale((l) => tr(l)('mark.capitulation', p)),
        };
      }
      case 'gameOver': {
        const winners = ev.winners.map((f) => this.nation(f)).filter((n): n is NationRef => n !== null);
        return {
          tick: ev.tick,
          kind: 'end',
          hex: -1,
          faction: ev.winners[0] ?? -1,
          text: inEveryLocale((l) => tr(l)('mark.end', { winners })),
        };
      }
      default:
        return null;
    }
  }
}

/**
 * Every staff's routine signals traffic — noise when all of it prints on one teletype (MET
 * warnings too: each staff gets its own; the public ground notices still print).
 */
const ROUTINE: ReadonlySet<GameEvent['kind']> = new Set<GameEvent['kind']>([
  'orderDelivered',
  'orderExecuted',
  'contact',
  'techResearched',
  'weatherForecast',
]);

/**
 * Whether an event belongs on the omniscient teletype: no routine signals traffic of every
 * staff, and a KESSEL only once (the encircled side's copy; watching enemies get their own).
 */
export function forTheRecord(ev: GameEvent): boolean {
  if (ROUTINE.has(ev.kind)) return false;
  if (ev.kind === 'encircled') return ev.audience === 'all' || ev.audience.includes(ev.faction);
  return true;
}

/** The marker nearest to `tick` within `radius` ticks (hover readout), or null. */
export function markerNear(
  markers: readonly ReplayMarker[],
  tick: number,
  radius: number,
): ReplayMarker | null {
  let best: ReplayMarker | null = null;
  for (const m of markers) {
    const d = Math.abs(m.tick - tick);
    if (d <= radius && (!best || d < Math.abs(best.tick - tick))) best = m;
  }
  return best;
}

/** First marker strictly after / before `tick` (the "next moment" buttons), or null. */
export function adjacentMarker(
  markers: readonly ReplayMarker[],
  tick: number,
  dir: 1 | -1,
): ReplayMarker | null {
  let best: ReplayMarker | null = null;
  for (const m of markers) {
    if (dir > 0 ? m.tick <= tick : m.tick >= tick) continue;
    if (!best || (dir > 0 ? m.tick < best.tick : m.tick > best.tick)) best = m;
  }
  return best;
}
